import React, { useState } from 'react';
import { BodyText, Button, Message, Stack, Title } from '@kvdbil/components';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import TextFieldWithControl from '~/App/shared/components/Fields/TextFieldWithControl';
import {
  member as memberSelector,
  idToken as idTokenSelector
} from '~/App/shared/selectors/sessionSelector';
import { getCountryFromValue } from '~/helpers/countries';
import { useTranslation } from '~/Locale';
import {
  fieldPropsAddress,
  fieldPropsCity,
  fieldPropsPostalCode
} from '~/App/shared/components/Fields/shared/fieldProps';
import { CountryField } from '~/App/shared/components/Fields/shared';
import { updateMemberSimple } from '~/helpers/orchestration/member';
import { sessionUpdate } from '~/App/shared/actions/session';

type Form = {
  address: string;
  zipCode: string;
  city: string;
  country: {
    label: string;
    value: string;
  };
  nationalIdentificationNumber: string;
};

interface Props {
  handleClose(): void;
}

const BiddingPrivMemberDataVerification = ({ handleClose }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const member = useSelector(memberSelector);
  const address = member?.addresses?.[0];
  const idToken = useSelector(idTokenSelector);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  const { handleSubmit, control, watch } = useForm<Form>({
    defaultValues: {
      address: address?.address1 ?? '',
      zipCode: address?.zipCode ?? '',
      city: address?.city ?? '',
      country: getCountryFromValue(address?.country ?? '', t) ?? {
        value: '',
        label: ''
      },
      nationalIdentificationNumber:
        member?.person?.nationalIdentificationNumber ?? ''
    }
  });

  const countryValue = watch('country')?.value;

  const onSubmit = async (formData: Form) => {
    isError && setIsError(false);
    setIsLoading(true);

    const { address, zipCode, city, country, nationalIdentificationNumber } =
      formData;
    const person = nationalIdentificationNumber
      ? {
          nationalIdentificationNumber,
          nationalIdentificationCountry: country?.value
        }
      : undefined;

    try {
      const { data } = await updateMemberSimple({
        data: {
          addresses: [
            { address1: address, zipCode, city, country: country?.value }
          ],
          person
        },
        idToken
      });

      dispatch(
        sessionUpdate({
          auth: data.auth,
          member: data.member,
          t
        })
      );
      handleClose();
    } catch (err: unknown) {
      setIsError(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Stack spacing={2} data-testid="customer-member-data-verification">
      <Stack>
        <Title as="h3" style={{ textAlign: 'center' }}>
          {t('Verify your information')}
        </Title>
        <BodyText>{t('You must verify your information to proceed.')}</BodyText>
      </Stack>
      <Stack>
        <BodyText>
          {t('Enter the address you want linked to your account.')}
        </BodyText>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Stack spacing={2}>
            <Stack>
              <TextFieldWithControl
                control={control}
                {...fieldPropsAddress({ t })}
              />
              <TextFieldWithControl
                control={control}
                {...fieldPropsPostalCode({ t, country: countryValue })}
              />
              <TextFieldWithControl
                control={control}
                {...fieldPropsCity({ t })}
              />
              <CountryField
                control={control}
                t={t}
                isDisabled={Boolean(address?.country)}
              />
              <TextFieldWithControl
                control={control}
                name="nationalIdentificationNumber"
                label={t('National identification number (optional)')}
                helperText={t(
                  'Entering this information will speed up the process when buying a vehicle.'
                )}
              />
            </Stack>
            {isError && (
              <Message type="error" fullWidth>
                {t('Something went wrong, please try again later.')}
              </Message>
            )}
            <Button
              color="secondary"
              size="regular"
              fullWidth
              type="submit"
              isLoading={isLoading}
            >
              {t('Save')}
            </Button>
          </Stack>
        </form>
      </Stack>
    </Stack>
  );
};

export default BiddingPrivMemberDataVerification;
